import { padEnd } from 'lodash';
import moment from 'moment';

import type {
  BillingInterval,
  CMSPlanType,
  LicenceType,
  ManagedSubscription,
  PersistedStripePrice,
  PlanTransitionType,
  SanitizedManagedSubscription,
  SanitizedSelfServeSubscription,
  SanitizedStripeSource,
  SanitizedSubscription,
  SelfServeSubscription,
} from '@sine/sine-core-api-types';

import { BillingIntervalValues } from '../../services/billing/billing.constants';
import type {
  BadgeInfo,
  Currency,
  HydratedBillableSite,
  LineItem,
  LineItemAggregation,
  ManageTableRecord,
  PlanSelectOption,
} from '../../services/billing/billing.types';
import { LICENSE_TYPE, LineItemAggregationGroupType } from '../../services/billing/billing.types';
import {
  BILLING_CYCLE,
  CMSPlanOptionsObject,
  CMS_TIERS,
  ENTERPRISE_MANUAL_LICENCE_TYPE,
  NEW_MANUAL_LICENCE_TYPE,
  PLAN_COMPONENTS_MAP,
  type StripePriceBadgeName,
  VMS_TIERS,
  currencies,
  licenceTypeToBadgeNameMap,
  planTierRanking,
} from './constants/billing.constants';

/**
 * Compares two plan types to determine tier changes for both VMS and CMS components.
 *
 * For combo plans (e.g., VMSLITE_CMSLITE), each product is evaluated independently:
 * - VMS component: VMSLITE, VMSPLUS, or VMSPREMIUM
 * - CMS component: CMSLITE or CMSPLUS
 *
 * @returns Object with vmsChange and cmsChange:
 *   - Positive values indicate an upgrade (higher tier)
 *   - Negative values indicate a downgrade (lower tier)
 *   - Zero indicates no change for that component
 *
 * @example
 * comparePlanTiers('VMSPREMIUM', 'VMSLITE_CMSLITE')
 * Returns: { vmsChange: -2, cmsChange: 1 }
 * VMS downgrade (Premium→Lite) and CMS upgrade (none→Lite)
 */
function comparePlanTiers(currentPlanType: string, newPlanType: string) {
  const lowerCurrent = currentPlanType.toLowerCase();
  const lowerNew = newPlanType.toLowerCase();

  const current = PLAN_COMPONENTS_MAP[lowerCurrent];
  const newPlan = PLAN_COMPONENTS_MAP[lowerNew];

  const currentVmsTier = VMS_TIERS[current.vms];
  const newVmsTier = VMS_TIERS[newPlan.vms];

  const currentCmsTier = CMS_TIERS[current.cms];
  const newCmsTier = CMS_TIERS[newPlan.cms];

  return {
    vmsChange: newVmsTier - currentVmsTier,
    cmsChange: newCmsTier - currentCmsTier,
  };
}

export function hasAnyDowngrade(currentPlanType: string, newPlanType: string): boolean {
  const { vmsChange, cmsChange } = comparePlanTiers(currentPlanType, newPlanType);
  return vmsChange < 0 || cmsChange < 0;
}

export function hasAnyUpgrade(currentPlanType: string, newPlanType: string): boolean {
  const { vmsChange, cmsChange } = comparePlanTiers(currentPlanType, newPlanType);
  return vmsChange > 0 || cmsChange > 0;
}

export function getCardExpiry(card: SanitizedStripeSource) {
  if (card.exp_month && card.exp_year) {
    return `0${card.exp_month}`.slice(-2) + '/' + card.exp_year;
  } else {
    return '';
  }
}

export function getCurrencySymbol(currency: Currency) {
  return currencies[currency].symbol || '$';
}

export function formatCurrencyValue(currency: Currency, value: string | number | undefined = '') {
  const [dollars, cents] = `${value}`.split('.');
  return `${getCurrencySymbol(currency)}${formatNumberWithCommas(dollars)}.${padEnd(cents, 2, '0')}`;
}

export function formatNumberWithCommas(value: string): string {
  if (!value) return value;
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatCentsToDollarWithCommas(value = 0) {
  const dollars = centsToDollars(value);
  return formatNumberWithCommas(dollars.toString());
}

export function centsToDollars(value = 0) {
  return Number((value / 100).toFixed(2));
}

export function calculatePlansPriceGap(initialPlanTierPrice: number, targetPlanTierPrice: number) {
  const priceDeltaInCents = Number((targetPlanTierPrice - initialPlanTierPrice).toFixed(2));
  const priceDeltaInDollars = centsToDollars(priceDeltaInCents);

  if (priceDeltaInDollars === 0) {
    return '0';
  }

  const signPrefix = priceDeltaInDollars > 0 ? '+' : '';

  return `${signPrefix}${formatNumberWithCommas(priceDeltaInDollars.toString())}`;
}

export function formatPriceWithInterval(shouldCalculatePriceGap: boolean, aggregation: LineItemAggregation) {
  const initialPrice = aggregation.initialPlanOption?.price?.amount || 0;
  const selectedPrice = aggregation.selectedPlanOption?.price?.amount || 0;
  const interval = aggregation.selectedPlanOption?.price?.interval;

  const price = shouldCalculatePriceGap
    ? calculatePlansPriceGap(initialPrice, selectedPrice)
    : formatCentsToDollarWithCommas(selectedPrice);

  const intervalText =
    interval === BillingIntervalValues.YEARLY ? 'year' : interval === BillingIntervalValues.MONTHLY ? 'month' : 'day';

  return `${price} / ${intervalText}`;
}

export const getLicenceSpotIconType = (type?: string) => {
  switch (type) {
    case 'small':
    case 'vmslite':
    case 'vmslite_cmslite':
    case 'vmslite_cmsplus':
      return 'site-small';

    case 'medium':
    case 'vmsplus':
    case 'vmsplus_cmslite':
    case 'vmsplus_cmsplus':
      return 'site-medium';

    case 'large':
    case 'vmspremium':
    case 'vmspremium_cmslite':
    case 'vmspremium_cmsplus':
    case 'enterprise':
      return 'site-large';

    case 'ent100':
    case 'unlimited':
      return 'site-enterprise';

    default:
      return 'site-large';
  }
};

export default function formatStripeDate(date: Date | string | number | null) {
  if (typeof date === 'number') {
    date = date * 1000;
  }

  return moment(date).format('D MMM YYYY');
}

export function formatStripeExpiryDate(date: string | number | undefined) {
  // Stripe date is a Unix timestamp
  const SECONDS_TO_MILLISECONDS = 1000;

  if (typeof date === 'number') {
    date = date * SECONDS_TO_MILLISECONDS;
  }

  return moment(date).format('MMM DD, YYYY');
}

export const mapLicenseTypeToSelectOption = (
  licenseType: string | undefined | null,
):
  | {
      vmsPlanType: `${LicenceType}`;
      addContractors: boolean;
      cmsPlanType: `${CMSPlanType}` | undefined;
    }
  | undefined => {
  if (!licenseType) {
    return undefined;
  }

  const reverseLicenseTypeMap = {
    [LICENSE_TYPE.VMSLITE]: {
      vmsPlanType: 'vmslite',
      addContractors: false,
      cmsPlanType: undefined,
    },
    [LICENSE_TYPE.VMSLITE_CMSLITE]: {
      vmsPlanType: 'vmslite',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_LITE.type,
    },
    [LICENSE_TYPE.VMSLITE_CMSPLUS]: {
      vmsPlanType: 'vmslite',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_PLUS.type,
    },

    [LICENSE_TYPE.VMSPLUS]: {
      vmsPlanType: 'vmsplus',
      addContractors: false,
      cmsPlanType: undefined,
    },
    [LICENSE_TYPE.VMSPLUS_CMSLITE]: {
      vmsPlanType: 'vmsplus',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_LITE.type,
    },
    [LICENSE_TYPE.VMSPLUS_CMSPLUS]: {
      vmsPlanType: 'vmsplus',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_PLUS.type,
    },

    [LICENSE_TYPE.VMSPREMIUM]: {
      vmsPlanType: 'vmspremium',
      addContractors: false,
      cmsPlanType: undefined,
    },
    [LICENSE_TYPE.VMSPREMIUM_CMSLITE]: {
      vmsPlanType: 'vmspremium',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_LITE.type,
    },
    [LICENSE_TYPE.VMSPREMIUM_CMSPLUS]: {
      vmsPlanType: 'vmspremium',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_PLUS.type,
    },
    [LICENSE_TYPE.VMSENTERPRISE]: {
      vmsPlanType: 'vmsenterprise',
      addContractors: false,
      cmsPlanType: undefined,
    },
    [LICENSE_TYPE.VMSENTERPRISE_CMSLITE]: {
      vmsPlanType: 'vmsenterprise',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_LITE.type,
    },
    [LICENSE_TYPE.VMSENTERPRISE_CMSPLUS]: {
      vmsPlanType: 'vmsenterprise',
      addContractors: true,
      cmsPlanType: CMSPlanOptionsObject.CMS_PLUS.type,
    },
  } satisfies Record<
    string,
    { vmsPlanType: `${LicenceType}`; addContractors: boolean; cmsPlanType: `${CMSPlanType}` | undefined }
  >;

  return reverseLicenseTypeMap[licenseType];
};

export function getPlanMonthlyPrice(amount?: number | null, interval?: BillingInterval) {
  switch (interval) {
    case BillingIntervalValues.DAILY:
      return centsToDollars((amount || 0 * 365) / 12);
    case BillingIntervalValues.MONTHLY:
      return centsToDollars(amount || 0);
    case BillingIntervalValues.YEARLY:
      return centsToDollars((amount || 0) / 12);
    default:
      return centsToDollars(0);
  }
}

export function getPlanName(price: PersistedStripePrice) {
  const name = price.metadata.badge_name;

  return `${name}`;
}

export function getPlanDescription(plan: PersistedStripePrice, currency: Currency) {
  const billingCycle =
    plan.interval === BILLING_CYCLE.YEARLY ? 'annually' : plan.interval === BILLING_CYCLE.MONTHLY ? 'monthly' : 'daily';

  return `${formatCurrencyValue(
    currency,
    getPlanMonthlyPrice(plan.amount || 0, plan.interval),
  )} per month billed ${billingCycle}`;
}

export function formatPlanOptionLabel(selectedPlanOption: PlanSelectOption | undefined, currency: Currency) {
  if (selectedPlanOption) {
    if (selectedPlanOption.value === LICENSE_TYPE.TRIAL_SYSADMIN) {
      return selectedPlanOption;
    }

    if (selectedPlanOption.subscriptionId) {
      return {
        ...selectedPlanOption,
        label: `${selectedPlanOption.type}`,
      };
    }
    const badgeName = selectedPlanOption.price?.metadata.badge_name;
    return {
      ...selectedPlanOption,
      label: `${badgeName} - ${formatCurrencyValue(
        currency,
        getPlanMonthlyPrice(selectedPlanOption.price?.amount || 0, selectedPlanOption.price?.interval),
      )} per month`,
    };
  }
}

export function getSubscriptionType(subscription: SanitizedSubscription) {
  switch (subscription.type) {
    case 'stripe': {
      return subscription.externalData.interval === 'month' ? 'Monthly' : 'Annual';
    }
    case 'manual':
    default: {
      return 'Managed';
    }
  }
}

export function getSubscriptionStart(subscription: SanitizedSubscription) {
  switch (subscription.type) {
    case 'stripe': {
      return formatStripeDate(subscription.created);
    }
    case 'manual':
    default: {
      return formatStripeDate(subscription.valid_from);
    }
  }
}

export function getSubscriptionExpiry(subscription: SanitizedSubscription) {
  switch (subscription.type) {
    case 'stripe': {
      return formatStripeDate(subscription.externalData.current_period_end);
    }
    case 'manual':
    default: {
      return formatStripeDate(subscription.valid_to);
    }
  }
}

function getBadgeInfo(subscriptions: Array<SanitizedSubscription>, sites: Array<HydratedBillableSite>) {
  const badgeInfo = subscriptions.reduce((info: Array<BadgeInfo>, subscription) => {
    subscription.licences?.forEach((license) => {
      if (!license.deleted) {
        const { externalData } = license;
        const badgeName = externalData?.plan?.metadata?.badge_name;

        const newBadgeInfoIndex = info.findIndex(
          (item) => item.badgeName === badgeName && item.subscriptionId === subscription.id,
        );

        const matchedSite = sites.find(({ id }) => id === license.site);

        const isUnassigned = !matchedSite || !license.site;

        if (isUnassigned && badgeName) {
          if (newBadgeInfoIndex === -1) {
            info.push({
              badgeName,
              count: 1,
              licenseIds: [license.id],
              subscriptionId: subscription.id,
            });
          } else {
            info[newBadgeInfoIndex] = {
              ...info[newBadgeInfoIndex],
              licenseIds: [...info[newBadgeInfoIndex].licenseIds, license.id],
              count: info[newBadgeInfoIndex].count + 1,
            };
          }
        }
      }
    });

    return info;
  }, []);

  return badgeInfo;
}

function getRenewDateOption(subscription: SanitizedSubscription) {
  const { current_period_end = undefined, cancel_at_period_end = undefined } =
    'externalData' in subscription ? subscription.externalData : {};

  const subscriptionType =
    getSubscriptionType(subscription) === 'Annual' ? 'Annually' : getSubscriptionType(subscription);
  const renewalLabel = cancel_at_period_end
    ? `Expires ${formatStripeExpiryDate(current_period_end)} (No renewal)`
    : `Renews ${formatStripeExpiryDate(current_period_end)} (${subscriptionType}) `;
  return {
    label: renewalLabel,
    value: subscription.id,
  };
}

function getRenewDateOptions(subscriptions: Array<SanitizedSubscription>) {
  const renewDateOptions = [];
  if (subscriptions.length) {
    for (const subscription of subscriptions) {
      const renewDateOption = getRenewDateOption(subscription);

      renewDateOptions.push(renewDateOption);
    }
  }

  return renewDateOptions;
}

export function getExistingPlanOptions(
  subscriptions: Array<SanitizedSubscription>,
  sites: Array<HydratedBillableSite>,
) {
  const badgeInfo = getBadgeInfo(subscriptions, sites);
  const allOptions = badgeInfo.map(({ badgeName, count, licenseIds, subscriptionId }) => ({
    label: `${badgeName} (${count} remaining)`,
    value: subscriptionId,
    subscriptionId: subscriptionId,
    licenseIds,
    count,
    type: badgeName,
  }));

  const renewDateOptions = getRenewDateOptions(
    subscriptions.filter((subscription) => {
      return badgeInfo.some((info) => info.subscriptionId === subscription.id);
    }),
  );

  return { allOptions, renewDateOptions };
}

export function updateNumberOfExistingPlanOptions({
  selectedPlanOption,
  lineItem,
  remainingExistingOptions,
}: {
  selectedPlanOption?: PlanSelectOption;
  lineItem: LineItem;
  remainingExistingOptions: Array<PlanSelectOption>;
}) {
  const updatedExistingOptions = remainingExistingOptions.map((option) => {
    const isSameSelectedOption =
      option.subscriptionId === selectedPlanOption?.subscriptionId && option.type === selectedPlanOption?.type;

    if (isSameSelectedOption) {
      const newCount = option.count! - 1;
      return {
        ...option,
        count: newCount,
        label: `${option.type} (${newCount} remaining)`,
      };
    }

    const isSelectedLineItem =
      lineItem.selectedPlanOption?.subscriptionId === option.subscriptionId &&
      lineItem.selectedPlanOption?.type === option.type;

    if (isSelectedLineItem) {
      const newCount = option.count! + 1;
      return {
        ...option,
        count: newCount,
        label: `${option.type} (${newCount} remaining)`,
      };
    }

    return option;
  });

  return updatedExistingOptions;
}

export function isSelfServeSubscription(
  subscription: SanitizedSubscription,
): subscription is SelfServeSubscription | SanitizedSelfServeSubscription {
  return subscription.type === 'stripe';
}

export function calculateTax(currency: Currency) {
  switch (currency) {
    case 'aud':
      return 0.1;
    case 'nzd':
      return 0.15;
    default:
      return 0;
  }
}

export const mapSelectOptionToLicenseType = (
  vmsPlanType: string,
  addContractors: boolean,
  cmsPlanType: string | undefined,
) => {
  const licenseTypeMap = {
    [NEW_MANUAL_LICENCE_TYPE.LITE.value]: {
      base: LICENSE_TYPE.VMSLITE,
      withContractors: {
        [CMSPlanOptionsObject.CMS_LITE.type]: LICENSE_TYPE.VMSLITE_CMSLITE,
        [CMSPlanOptionsObject.CMS_PLUS.type]: LICENSE_TYPE.VMSLITE_CMSPLUS,
      },
    },
    [NEW_MANUAL_LICENCE_TYPE.PLUS.value]: {
      base: LICENSE_TYPE.VMSPLUS,
      withContractors: {
        [CMSPlanOptionsObject.CMS_LITE.type]: LICENSE_TYPE.VMSPLUS_CMSLITE,
        [CMSPlanOptionsObject.CMS_PLUS.type]: LICENSE_TYPE.VMSPLUS_CMSPLUS,
      },
    },
    [NEW_MANUAL_LICENCE_TYPE.PREMIUM.value]: {
      base: LICENSE_TYPE.VMSPREMIUM,
      withContractors: {
        [CMSPlanOptionsObject.CMS_LITE.type]: LICENSE_TYPE.VMSPREMIUM_CMSLITE,
        [CMSPlanOptionsObject.CMS_PLUS.type]: LICENSE_TYPE.VMSPREMIUM_CMSPLUS,
      },
    },
    [ENTERPRISE_MANUAL_LICENCE_TYPE.ENTERPRISE.value]: {
      base: LICENSE_TYPE.VMSENTERPRISE,
      withContractors: {
        [CMSPlanOptionsObject.CMS_LITE.type]: LICENSE_TYPE.VMSENTERPRISE_CMSLITE,
        [CMSPlanOptionsObject.CMS_PLUS.type]: LICENSE_TYPE.VMSENTERPRISE_CMSPLUS,
      },
    },
  };

  const mapping = licenseTypeMap[vmsPlanType];

  if (!addContractors || !cmsPlanType) {
    return mapping.base;
  }

  return mapping.withContractors[cmsPlanType as keyof typeof mapping.withContractors];
};

export function getBillingCycle(subscription: SanitizedSelfServeSubscription) {
  return (
    subscription.externalData.interval ||
    subscription.licences?.[0]?.externalData?.price?.interval ||
    subscription.licences?.[0]?.externalData?.plan?.interval ||
    BillingIntervalValues.YEARLY
  );
}

export function buildLicenceRecords({
  sites,
  trials,
  subscription,
  unlicencedSites,
  targetRecord,
  includeTrials = true,
  includeOngoingLicences = true,
  includeUnlicensedSites = true,
}: {
  sites: Array<HydratedBillableSite>;
  trials: Array<SanitizedSubscription>;
  subscription?: SanitizedSubscription;
  unlicencedSites: Array<HydratedBillableSite>;
  targetRecord?: ManageTableRecord;
  includeTrials?: boolean;
  includeOngoingLicences?: boolean;
  includeUnlicensedSites?: boolean;
}) {
  const records = [];

  if (targetRecord) {
    return [targetRecord];
  }

  if (includeTrials) {
    records.push(
      ...trials.flatMap((trial) => {
        return (
          trial.licences?.map((licence) => ({
            licence: {
              ...licence,
              expiry: getSubscriptionExpiry(trial),
              billingCycle: null,
              type: trial.type,
              licenceType: licence.type,
            },
            site: sites.find((site) => site.id === licence.site),
          })) || []
        );
      }),
    );
  }

  if (includeOngoingLicences) {
    records.push(
      ...(subscription?.licences?.map((licence) => {
        return {
          licence: {
            ...licence,
            expiry: getSubscriptionExpiry(subscription),
            startDate: getSubscriptionStart(subscription),
            billingCycle: isSelfServeSubscription(subscription) ? getBillingCycle(subscription) : null,
            type: subscription.type,
            licenceType:
              licence.type || licence.externalData?.price?.metadata?.type || licence.externalData?.plan?.metadata?.type,
          },
          site: sites.find((site) => site.id === licence.site),
        };
      }) || []),
    );
  }

  if (includeUnlicensedSites) {
    records.push(
      ...unlicencedSites.map((site: HydratedBillableSite) => {
        return {
          licence: undefined,
          site,
        };
      }),
    );
  }

  return records;
}

export function isTrial(type?: string | null) {
  return [`${LICENSE_TYPE.TRIAL_BASIC}`, `${LICENSE_TYPE.TRIAL_SYSADMIN}`, `${LICENSE_TYPE.TRIAL_30_DAYS}`].includes(
    type || '',
  );
}

export function mapLicenceTypeToStripeBadgeName(
  licenceType: string | undefined | null,
): StripePriceBadgeName | undefined {
  if (!licenceType) {
    return undefined;
  }

  return licenceTypeToBadgeNameMap[licenceType];
}

export function getLineItemAggregationGroupType(lineItem: LineItem): LineItemAggregationGroupType | undefined {
  const { id, selectedPlanOption, initialSelectedPlanOption, licenceType } = lineItem;

  if (
    (selectedPlanOption && !id) ||
    licenceType === LICENSE_TYPE.TRIAL_SYSADMIN ||
    licenceType === LICENSE_TYPE.TRIAL_30_DAYS
  ) {
    return LineItemAggregationGroupType.NEW;
  }

  const initialPlanOptionBadgeName = initialSelectedPlanOption?.price?.metadata.badge_name;
  const selectedPlanOptionBadgeName = selectedPlanOption?.price?.metadata.badge_name;

  if (!initialPlanOptionBadgeName || !selectedPlanOptionBadgeName) {
    return undefined;
  }

  const savedRank = planTierRanking[initialPlanOptionBadgeName];
  const newRank = planTierRanking[selectedPlanOptionBadgeName];

  if (!savedRank || !newRank) {
    return undefined;
  }

  if (newRank === savedRank) {
    return LineItemAggregationGroupType.EXISTING;
  }

  return newRank > savedRank ? LineItemAggregationGroupType.UPGRADE : LineItemAggregationGroupType.DOWNGRADE;
}

export function getPlanTiers(planType: string): { vms: string; cms: string } {
  const lowerPlan = planType.toLowerCase();
  const tiers = PLAN_COMPONENTS_MAP[lowerPlan];

  return tiers;
}

/**
 * Compares two plans and determines the change type for each product independently.
 * Returns an object with separate change types for VMS and CMS:
 * - 'upgrade' if the tier is higher
 * - 'downgrade' if the tier is lower
 * - 'same' if no change
 *
 * VMS and CMS are evaluated independently per the acceptance criteria:
 * - VMS upgrade/downgrade is handled separately
 * - CMS addition/removal/tier change is handled separately
 * - Each product follows its own upgrade/downgrade rules
 *
 * Examples:
 * - VMSPREMIUM → VMSLITE_CMSLITE: { vms: 'downgrade', cms: 'upgrade' }
 * - VMSPLUS_CMSPLUS → VMSPREMIUM_CMSLITE: { vms: 'upgrade', cms: 'downgrade' }
 */
export function comparePlans(
  currentPlanType: string,
  newPlanType: string,
): { vms: PlanTransitionType; cms: PlanTransitionType } {
  const current = getPlanTiers(currentPlanType);
  const newPlan = getPlanTiers(newPlanType);

  // Get tiers from the constants
  const currentVmsTier = VMS_TIERS[current.vms];
  const newVmsTier = VMS_TIERS[newPlan.vms];

  const currentCmsTier = CMS_TIERS[current.cms];
  const newCmsTier = CMS_TIERS[newPlan.cms];

  // Determine VMS change
  let vmsChange: PlanTransitionType;
  if (newVmsTier > currentVmsTier) {
    vmsChange = 'upgrade';
  } else if (newVmsTier < currentVmsTier) {
    vmsChange = 'downgrade';
  } else {
    vmsChange = 'same';
  }

  // Determine CMS change
  let cmsChange: PlanTransitionType;
  if (newCmsTier > currentCmsTier) {
    cmsChange = 'upgrade';
  } else if (newCmsTier < currentCmsTier) {
    cmsChange = 'downgrade';
  } else {
    cmsChange = 'same';
  }

  return { vms: vmsChange, cms: cmsChange };
}

/**
 * Calculates the plan transition strategy for independent VMS and CMS handling.
 *
 * When a plan change involves both upgrades and downgrades:
 * - Upgrades are applied immediately
 * - Downgrades are scheduled for the next renewal period
 *
 * This function returns the intermediate plan (with upgrades applied) and the final plan (with all changes).
 *
 * Examples:
 * - VMSPREMIUM → VMSLITE_CMSLITE:
 *   - Immediate: VMSPREMIUM_CMSLITE (CMS upgrade applied now)
 *   - Scheduled: VMSLITE_CMSLITE (VMS downgrade at renewal)
 *
 * - VMSLITE_CMSPLUS → VMSPREMIUM:
 *   - Immediate: VMSPREMIUM_CMSPLUS (VMS upgrade applied now)
 *   - Scheduled: VMSPREMIUM (CMS downgrade at renewal)
 *
 * - VMSLITE → VMSPREMIUM_CMSLITE:
 *   - Immediate: VMSPREMIUM_CMSLITE (both are upgrades, applied now)
 *   - Scheduled: null (no downgrades to schedule)
 *
 * @returns Object with immediatePlan and scheduledPlan (null if no downgrades)
 */
export function calculatePlanTransition(
  currentPlanType: string,
  targetPlanType: string,
): {
  immediatePlan: string | null;
  scheduledPlan: string | null;
} {
  const comparison = comparePlans(currentPlanType, targetPlanType);
  const current = getPlanTiers(currentPlanType);
  const target = getPlanTiers(targetPlanType);

  const hasUpgrades = comparison.vms === 'upgrade' || comparison.cms === 'upgrade';
  const hasDowngrades = comparison.vms === 'downgrade' || comparison.cms === 'downgrade';

  // If no changes at all, return nulls
  if (!hasUpgrades && !hasDowngrades) {
    return {
      immediatePlan: null,
      scheduledPlan: null,
    };
  }

  // If only upgrades (no downgrades), apply everything immediately
  if (hasUpgrades && !hasDowngrades) {
    return {
      immediatePlan: targetPlanType,
      scheduledPlan: null,
    };
  }

  // If only downgrades (no upgrades), schedule everything for renewal
  if (!hasUpgrades && hasDowngrades) {
    return {
      immediatePlan: null,
      scheduledPlan: targetPlanType,
    };
  }

  // Mixed scenario: has both upgrades and downgrades
  // Build intermediate plan with upgrades applied, keeping current values for downgrades
  const intermediateVms = comparison.vms === 'upgrade' ? target.vms : current.vms;
  const intermediateCms = comparison.cms === 'upgrade' ? target.cms : current.cms;

  // Construct the intermediate plan type
  let immediatePlan: string;
  if (intermediateCms === 'none') {
    immediatePlan = intermediateVms;
  } else {
    immediatePlan = `${intermediateVms}_${intermediateCms}`;
  }

  return {
    immediatePlan,
    scheduledPlan: targetPlanType,
  };
}

export const getChangedLineItems = (lineItem: LineItem, initialLineItem: Array<LineItem>) => {
  const matchingInitialItem = initialLineItem.find((initial) => initial.id === lineItem.id);

  if (lineItem.isNew) return true;

  return lineItem.selectedPlanOption?.value !== matchingInitialItem?.selectedPlanOption?.value;
};

export function isManagedSubscription(
  subscription: SanitizedSubscription,
): subscription is ManagedSubscription | SanitizedManagedSubscription {
  return subscription.type === 'manual';
}
